import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import PlayerCard from "@/components/PlayerCard";
import LeadForm from "@/components/LeadForm";
import { api } from "@/lib/api";
import { UserPlus, Search, Gavel, Star, Route as RouteIcon } from "lucide-react";

const BLOCKS = [
  { Icon: UserPlus, title: "Player Registration", desc: "Online registration and trial events for athletes 16–35 across India." },
  { Icon: Search,   title: "Talent Scouting",     desc: "PSHL scouts on the ground in 12 cities + academy partner pipeline." },
  { Icon: Gavel,    title: "Draft / Auction",     desc: "Live PSHL8 draft and auction event for all eight franchises." },
  { Icon: Star,     title: "PSHL8 Player Rating", desc: "Proprietary 8-pillar rating system across the league." },
  { Icon: RouteIcon,title: "Academy Pathway",     desc: "Direct pipeline from PSHL academies to franchise contracts." },
];

const POSITIONS = ["ALL", "GK", "DEF", "MID", "FWD"];

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [posFilter, setPosFilter] = useState("ALL");

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, []);

  const teamsBySlug = useMemo(() => Object.fromEntries(teams.map((t) => [t.slug, t])), [teams]);

  const filtered = useMemo(() => {
    return players.filter((p) =>
      (teamFilter === "ALL" || p.team === teamFilter) &&
      (posFilter === "ALL" || p.position === posFilter)
    );
  }, [players, teamFilter, posFilter]);

  return (
    <div>
      <PageMeta
        title="Players & Draft — From Talent to Trophy"
        description="Trials, scouting, the PSHL8 draft, player rating and the pro pathway."
        image="/api/og/players/og-image.png"
      />
      <PageHero
        overline="Players & Draft"
        title={<>From Talent to <span className="text-pshl-electric">Trophy.</span></>}
        subtitle="Trials, scouting, draft, rating and the pro pathway — all run by the PSHL Players Office."
        bgImage="https://images.pexels.com/photos/8974846/pexels-photo-8974846.jpeg"
      />

      <section className="section pt-0">
        <div className="pshl-container grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {BLOCKS.map(({ Icon, title, desc }, i) => (
            <div key={title} data-testid={`players-block-${i}`} className="bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors p-6">
              <div className="h-10 w-10 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40"><Icon className="h-5 w-5 text-pshl-electric" /></div>
              <div className="font-display text-xl text-white mt-4">{title}</div>
              <div className="text-sm text-white/65 mt-2 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Roster */}
      <section className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Inaugural Roster</div>
          <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">96 Players. <span className="text-white/40">8 Franchises.</span></h2>

          {/* Filters */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="player-filters">
            <button data-testid="player-team-filter-all" onClick={() => setTeamFilter("ALL")} className={`text-[11px] font-display uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${teamFilter === "ALL" ? "bg-pshl-electric border-pshl-electric text-white" : "border-white/15 text-white/80 hover:border-white"}`}>All Teams</button>
            {teams.map((t) => (
              <button key={t.slug} data-testid={`player-team-filter-${t.slug}`} onClick={() => setTeamFilter(t.slug)} className={`text-[11px] font-display uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${teamFilter === t.slug ? "bg-pshl-electric border-pshl-electric text-white" : "border-white/15 text-white/80 hover:border-white"}`}>{t.abbr}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {POSITIONS.map((p) => (
              <button key={p} data-testid={`player-pos-filter-${p.toLowerCase()}`} onClick={() => setPosFilter(p)} className={`text-[11px] font-display uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${posFilter === p ? "bg-pshl-gold border-pshl-gold text-black" : "border-white/15 text-white/80 hover:border-white"}`}>{p === "ALL" ? "All Positions" : p}</button>
            ))}
          </div>

          {/* Grid */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((p, i) => <PlayerCard key={p.id} player={p} team={teamsBySlug[p.team]} index={i} />)}
          </div>
          {filtered.length === 0 && <div className="mt-8 text-white/60">No players match this filter.</div>}
        </div>
      </section>

      <section className="section">
        <div className="pshl-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Register</div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Register as a Player.</h2>
            <p className="mt-4 text-white/75 leading-relaxed">Submit your details and our scouting team will reach out with trial windows in your city.</p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm defaultInterest="player" title="Player Registration" subtitle="We respond within 24 hours." />
          </div>
        </div>
      </section>
    </div>
  );
}
